import type { AIResponseDraft, AIResponseOperation, ExampleResponseEnvelope } from '../../entities/ai-assist-config';
import { createEmptyResponseDraft } from '../../entities/ai-assist-config';
import { isPlainObject, validateAINodeFieldObject } from './ai-node-field-validation';

export function validateAIResponse(rawText: string): AIResponseDraft {
  const draft = createEmptyResponseDraft();
  draft.rawText = rawText;

  if (!rawText.trim()) {
    return draft;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(rawText);
  } catch {
    return invalidDraft(rawText, 'invalid-json', ['Response must be valid JSON only.']);
  }

  const issues = validateEnvelope(parsed);
  if (issues.length > 0) {
    return invalidDraft(rawText, 'invalid-contract', issues);
  }

  return {
    rawText,
    parsedEnvelope: parsed as ExampleResponseEnvelope,
    validationStatus: 'valid',
    issues: [],
    validatedAt: new Date().toISOString(),
  };
}

function invalidDraft(rawText: string, validationStatus: AIResponseDraft['validationStatus'], issues: string[]): AIResponseDraft {
  return {
    rawText,
    parsedEnvelope: null,
    validationStatus,
    issues,
    validatedAt: new Date().toISOString(),
  };
}

function validateEnvelope(parsed: unknown): string[] {
  const issues: string[] = [];
  if (!isPlainObject(parsed)) {
    return ['Top-level response must be a JSON object.'];
  }

  const envelope = parsed as Partial<ExampleResponseEnvelope>;
  if (!Array.isArray(envelope.operations) || envelope.operations.length === 0) {
    issues.push('operations must be a non-empty array.');
    return issues;
  }

  envelope.operations.forEach((operation, index) => {
    issues.push(...validateOperation(operation as AIResponseOperation, index));
  });

  return issues;
}

function validateOperation(operation: AIResponseOperation, index: number): string[] {
  const issues: string[] = [];
  if (!operation || typeof operation !== 'object') {
    return [`operations[${index}] must be an object.`];
  }

  if (operation.action !== 'create' && operation.action !== 'update') {
    issues.push(`operations[${index}].action must be create or update.`);
  }
  if (!['Human', 'Project', 'Occupation'].includes(operation.nodeType)) {
    issues.push(`operations[${index}].nodeType is invalid.`);
  }
  if (operation.action === 'update' && !operation.nodeId) {
    issues.push(`operations[${index}].nodeId is required for update.`);
  }
  if (operation.action === 'create' && operation.nodeId) {
    issues.push(`operations[${index}].nodeId must be omitted for create.`);
  }
  if (!isPlainObject(operation.fields)) {
    issues.push(`operations[${index}].fields must be an object.`);
    return issues;
  }

  issues.push(...validateAINodeFieldObject(operation.nodeType, operation.fields, { pathPrefix: `operations[${index}].fields` }));

  return issues;
}
